import { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { useOrderStore, Order, OrderStatus } from '@/store/orderStore'
import { useUserStore } from '@/store/userStore'
import { FaCheckCircle, FaTimesCircle, FaClock, FaTruck, FaBox, FaSearch } from 'react-icons/fa'

const Orders = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const orderIdParam = searchParams.get('orderId')
  
  const { orders, getOrder, getOrdersByEmail } = useOrderStore()
  const { user } = useUserStore()
  
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [displayedOrders, setDisplayedOrders] = useState<Order[]>([])

  useEffect(() => {
    // If orderId param exists, show that order
    if (orderIdParam) {
      const order = getOrder(orderIdParam)
      if (order) {
        setSelectedOrder(order)
      }
    }

    // Filter orders by email if user is logged in
    if (user?.email) {
      setDisplayedOrders(getOrdersByEmail(user.email))
    } else {
      // Show all orders (for demo purposes)
      setDisplayedOrders(orders)
    }
  }, [orderIdParam, user, orders, getOrder, getOrdersByEmail])

  const getStatusIcon = (status: OrderStatus) => {
    switch (status) {
      case 'pending':
      case 'processing':
      case 'reservation_pending':
        return <FaClock className="w-5 h-5 text-yellow-400" />
      case 'confirmed':
      case 'reservation_approved':
        return <FaCheckCircle className="w-5 h-5 text-green-400" />
      case 'shipped':
        return <FaTruck className="w-5 h-5 text-blue-400" />
      case 'delivered':
        return <FaBox className="w-5 h-5 text-primary-400" />
      case 'cancelled':
      case 'reservation_rejected':
      case 'reservation_timeout':
        return <FaTimesCircle className="w-5 h-5 text-red-400" />
      default:
        return <FaClock className="w-5 h-5 text-gray-400" />
    }
  }

  const getStatusText = (status: OrderStatus) => {
    const statusMap: Record<OrderStatus, string> = {
      pending: t('orders.status.pending'),
      processing: t('orders.status.processing'),
      confirmed: t('orders.status.confirmed'),
      shipped: t('orders.status.shipped'),
      delivered: t('orders.status.delivered'),
      cancelled: t('orders.status.cancelled'),
      reservation_pending: t('orders.status.reservationPending'),
      reservation_approved: t('orders.status.reservationApproved'),
      reservation_rejected: t('orders.status.reservationRejected'),
      reservation_timeout: t('orders.status.reservationTimeout'),
    }
    return statusMap[status] || status
  }

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'pending':
      case 'processing':
      case 'reservation_pending':
        return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
      case 'confirmed':
      case 'reservation_approved':
        return 'bg-green-500/10 text-green-400 border-green-500/20'
      case 'shipped':
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20'
      case 'delivered':
        return 'bg-primary-500/10 text-primary-400 border-primary-500/20'
      case 'cancelled':
      case 'reservation_rejected':
      case 'reservation_timeout':
        return 'bg-red-500/10 text-red-400 border-red-500/20'
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20'
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (displayedOrders.length === 0) {
    return (
      <div className="bg-dark-950 min-h-screen py-12 relative overflow-hidden">
        <div className="fixed inset-0 bg-grid-pattern opacity-20"></div>
        <div className="absolute inset-0 bg-gradient-mesh opacity-30"></div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-black mb-4">
              <span className="text-gradient">{t('orders.title')}</span>
            </h1>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-primary-500 to-transparent mx-auto mb-8"></div>

            <div className="card-hover p-12 max-w-md mx-auto">
              <FaSearch className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-lg text-gray-400 mb-6">{t('orders.noOrders')}</p>
              <Link
                to="/shop"
                className="inline-block px-6 py-3 bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 hover:from-primary-600 hover:via-primary-700 hover:to-primary-800 text-white rounded-lg font-semibold transition-all duration-300 shadow-lg shadow-primary-500/50"
              >
                {t('orders.startShopping')}
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-dark-950 min-h-screen py-6 sm:py-8 relative overflow-hidden">
      <div className="fixed inset-0 bg-grid-pattern opacity-20"></div>
      <div className="absolute inset-0 bg-gradient-mesh opacity-30"></div>

      <div className="container-custom px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 sm:mb-8"
        >
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black mb-3 sm:mb-4">
            <span className="text-gradient bg-clip-text text-transparent bg-gradient-to-r from-primary-400 via-purple-400 to-primary-400">
              {t('orders.title')}
            </span>
          </h1>
          <p className="text-base sm:text-lg text-gray-400">
            {t('orders.subtitle', { count: displayedOrders.length })}
          </p>
          <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-primary-500 to-transparent mt-4"></div>
        </motion.div>

        {/* Orders List */}
        <div className="space-y-4">
          {displayedOrders
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
            .map((order, index) => (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card-hover p-6 cursor-pointer"
                onClick={() => setSelectedOrder(order)}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-100">
                        {t('orders.order')} #{order.id.split('-')[1] || order.id}
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold border flex items-center gap-2 ${getStatusColor(
                          order.status
                        )}`}
                      >
                        {getStatusIcon(order.status)}
                        {getStatusText(order.status)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 mb-2">
                      {formatDate(order.createdAt)}
                    </p>
                    <p className="text-sm text-gray-400">
                      {order.items.length} {t('orders.items')} • {order.totalPrice.toFixed(2)} €
                    </p>
                    {order.trackingCode && (
                      <p className="text-sm text-primary-400 mt-2">
                        {t('orders.trackingCode')}: {order.trackingCode}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-primary-400 mb-1">
                      {order.totalPrice.toFixed(2)} €
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedOrder(order)
                      }}
                      className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
                    >
                      {t('orders.viewDetails')}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
        </div>

        {/* Order Details Modal */}
        {selectedOrder && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedOrder(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-dark-900 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-100">
                  {t('orders.orderDetails')} #{selectedOrder.id.split('-')[1] || selectedOrder.id}
                </h2>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="text-gray-400 hover:text-gray-300 transition-colors"
                >
                  ✕
                </button>
              </div>

              {/* Status */}
              <div className="mb-6">
                <span
                  className={`px-4 py-2 rounded-lg text-sm font-semibold border inline-flex items-center gap-2 ${getStatusColor(
                    selectedOrder.status
                  )}`}
                >
                  {getStatusIcon(selectedOrder.status)}
                  {getStatusText(selectedOrder.status)}
                </span>
              </div>

              {/* Items */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-100 mb-3">{t('orders.items')}</h3>
                <div className="space-y-2">
                  {selectedOrder.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center p-3 bg-dark-800 rounded-lg"
                    >
                      <div>
                        <p className="text-gray-100 font-semibold">{item.productName}</p>
                        <p className="text-sm text-gray-400">
                          {t('orders.quantity')}: {item.quantity}
                        </p>
                      </div>
                      <p className="text-gray-200 font-semibold">
                        {(item.price * item.quantity).toFixed(2)} €
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Addresses */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-100 mb-2">{t('checkout.deliveryAddress')}</h3>
                  <div className="text-sm text-gray-400 space-y-1">
                    <p>
                      {selectedOrder.deliveryAddress.firstName} {selectedOrder.deliveryAddress.lastName}
                    </p>
                    <p>
                      {selectedOrder.deliveryAddress.street} {selectedOrder.deliveryAddress.houseNumber}
                    </p>
                    <p>
                      {selectedOrder.deliveryAddress.postalCode} {selectedOrder.deliveryAddress.city}
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-100 mb-2">{t('checkout.invoiceAddress')}</h3>
                  <div className="text-sm text-gray-400 space-y-1">
                    <p>
                      {selectedOrder.invoiceAddress.firstName} {selectedOrder.invoiceAddress.lastName}
                    </p>
                    <p>
                      {selectedOrder.invoiceAddress.street} {selectedOrder.invoiceAddress.houseNumber}
                    </p>
                    <p>
                      {selectedOrder.invoiceAddress.postalCode} {selectedOrder.invoiceAddress.city}
                    </p>
                  </div>
                </div>
              </div>

              {/* Total */}
              <div className="border-t border-dark-700 pt-4">
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-gray-100">{t('cart.total')}</span>
                  <span className="text-primary-400">{selectedOrder.totalPrice.toFixed(2)} €</span>
                </div>
              </div>

              {/* Reservation Info */}
              {selectedOrder.reservationPin && (
                <div className="mt-4 p-4 bg-primary-500/10 border border-primary-500/20 rounded-lg">
                  <p className="text-sm text-primary-400">
                    <strong>{t('orders.reservationPin')}:</strong> {selectedOrder.reservationPin}
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Orders
